/**
 * Auth slice - Redux state for auth
 * Fast reads from store, single API orchestration
 */
import { createSlice } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { AUTH, ROLES, USERS } from '../../api/endpoints';
import { logoutOneSignal } from '../../services/oneSignalPush';
import {
  clearAuthSessionCache,
  getFreshAuthSessionCache,
  writeAuthSessionCache,
} from '../../utils/authSessionCache';
import { clearLookupSessionCache } from '../../utils/lookupSessionCache';
import { clearUnreadNotifications, clearSessionScopedUserMeta } from './userSlice';
import { clearLeadsLookupMeta } from './leadsSlice';
import { clearLeadFormOptionsCache } from '../../components/Leads/leadFormOptionsCache';

/** Saved before swapping to an impersonated JWT so we can recover if /impersonate/leave returns no token field. */
const IMPERSONATION_PARENT_TOKEN_KEY = 'impersonation_parent_token';

/** In-flight dedupe for checkAuth (React Strict Mode / double mount). */
let checkAuthInFlight = null;

/** Drop session/local caches + user-scoped Redux so the next identity refetches APIs. */
const invalidateSessionScopedCaches = (dispatch) => {
  clearAuthSessionCache();
  clearLookupSessionCache();
  clearLeadFormOptionsCache();
  dispatch(clearUnreadNotifications());
  dispatch(clearSessionScopedUserMeta());
  dispatch(clearLeadsLookupMeta());
};

const persistAuthSessionCache = (user, permissions, impersonating) => {
  const token = localStorage.getItem('token');
  if (!token || !user) return;
  const roleId = user?.role?.id || user?.roles?.[0]?.id;
  writeAuthSessionCache({
    token,
    user,
    permissions: permissions || [],
    roleId,
    impersonating:
      impersonating === true || localStorage.getItem('impersonating') === 'true',
  });
};

/**
 * Parse login / impersonate / leave-impersonation responses — backends differ on nesting and field names.
 * @param {import('axios').AxiosResponse | Record<string, unknown>} axiosResponse - full axios response or plain body
 */
const extractAuthPayload = (axiosResponse) => {
  const body = axiosResponse && typeof axiosResponse === 'object' && 'data' in axiosResponse
    ? axiosResponse.data
    : axiosResponse;
  if (!body || typeof body !== 'object') {
    return { token: null, user: null };
  }
  const inner = body.data;
  const inner2 = inner && typeof inner === 'object' ? inner.data : null;

  const tokenCandidates = [
    body.access_token,
    body.token,
    body.plainTextToken,
    inner?.access_token,
    inner?.token,
    inner?.plainTextToken,
    inner2?.access_token,
    inner2?.token,
    inner2?.plainTextToken,
    body.authentication?.token,
    inner?.authentication?.token,
    inner2?.authentication?.token,
  ];
  const token =
    tokenCandidates.find((t) => typeof t === 'string' && t.length > 0) ?? null;

  const userCandidates = [
    body.user,
    inner?.user,
    inner2?.user,
    inner?.id != null || inner?.uuid != null ? inner : null,
    inner2?.id != null || inner2?.uuid != null ? inner2 : null,
  ];
  const user =
    userCandidates.find((u) => u && typeof u === 'object' && (u.id != null || u.uuid != null)) ?? null;

  const h = axiosResponse && typeof axiosResponse === 'object' && 'headers' in axiosResponse ? axiosResponse.headers : null;
  const authHeader = h?.authorization || h?.Authorization;
  let headerToken = null;
  if (typeof authHeader === 'string' && authHeader.trim()) {
    const m = authHeader.match(/^Bearer\s+(.+)$/i);
    headerToken = (m ? m[1] : authHeader).trim();
  }

  return { token: token || headerToken || null, user };
};

const getInitialState = () => {
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  if (token && userStr) {
    try {
      const user = JSON.parse(userStr);
      return {
        user,
        permissions: [],
        isAuthenticated: true,
        loading: true,
        permissionsReady: false,
        impersonating: localStorage.getItem('impersonating') === 'true',
      };
    } catch (e) {
      // Invalid JSON in localStorage, ignore
    }
  }
  return {
    user: null,
    permissions: [],
    isAuthenticated: false,
    loading: true,
    permissionsReady: false,
    impersonating: false,
  };
};

const initialState = getInitialState();

/** Normalize permission name for consistent matching (dba-create, dba create, dba_create) */
const normalizePermission = (name) =>
  (name || '').toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');

const extractPermissionNames = (response) => {
  const permsData = response?.data?.data?.permissions || response?.data || [];
  return (Array.isArray(permsData) ? permsData : [])
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Extract permission names from user object or profile response */
const extractUserPermissionNames = (userData) => {
  const perms = userData?.permissions ?? userData?.data?.permissions ?? [];
  const arr = Array.isArray(perms) ? perms : [];
  return arr
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Extract permissions from /auth/profile response - supports multiple shapes */
const extractProfilePermissions = (profileResponse) => {
  const data = profileResponse?.data ?? profileResponse;
  const user = data?.user ?? data?.data;
  const perms =
    user?.permissions ??
    data?.permissions ??
    data?.data?.permissions ??
    [];
  const arr = Array.isArray(perms) ? perms : [];
  return arr
    .map((p) => (typeof p === 'string' ? p : p?.name || p?.permission_name))
    .filter(Boolean)
    .map(normalizePermission);
};

/** Merge role permissions with user permissions (deduplicated) */
const mergePermissions = (rolePerms, userPerms) => {
  const set = new Set([...(rolePerms || []), ...(userPerms || [])]);
  return [...set];
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, { payload }) => {
      state.user = payload?.user ?? payload;
      state.isAuthenticated = !!payload;
      state.loading = false;
    },
    setPermissions: (state, { payload }) => {
      state.permissions = payload || [];
    },
    setPermissionsReady: (state, { payload }) => {
      state.permissionsReady = payload !== false;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload ?? true;
    },
    logout: (state) => {
      state.user = null;
      state.permissions = [];
      state.isAuthenticated = false;
      state.loading = false;
      state.permissionsReady = false;
      state.impersonating = false;
    },
    refreshPermissionsSuccess: (state, { payload }) => {
      state.permissions = payload || [];
    },
    setImpersonating: (state, { payload }) => {
      state.impersonating = payload === true;
    },
  },
});

export const {
  setCredentials,
  setPermissions,
  setPermissionsReady,
  setLoading,
  logout: logoutAction,
  refreshPermissionsSuccess,
  setImpersonating,
} = authSlice.actions;

export default authSlice.reducer;

/**
 * Persist token, clear impersonation flags, load /auth/profile and permissions into Redux.
 */
const hydrateSessionAfterTokenChange = async (dispatch, token, userFromBody) => {
  localStorage.setItem('token', token);
  localStorage.removeItem('impersonating');
  localStorage.removeItem(IMPERSONATION_PARENT_TOKEN_KEY);
  dispatch(setImpersonating(false));
  invalidateSessionScopedCaches(dispatch);

  const profileRes = await api.get(AUTH.PROFILE);
  const profileData = profileRes?.data ?? profileRes;
  const profileUser = profileData?.user ?? profileData?.data ?? profileData;
  // Leave/impersonate payloads sometimes repeat the impersonated `user` while the token is already
  // the admin session — always trust /auth/profile as canonical.
  if (profileUser) {
    dispatch(setCredentials(profileUser));
    localStorage.setItem('user', JSON.stringify(profileUser));
  } else if (userFromBody && typeof userFromBody === 'object' && (userFromBody.id || userFromBody.uuid)) {
    dispatch(setCredentials(userFromBody));
    localStorage.setItem('user', JSON.stringify(userFromBody));
  } else {
    dispatch(setCredentials({ token }));
  }

  const profilePerms = extractProfilePermissions(profileRes);
  const roleId = profileUser?.role?.id || profileUser?.roles?.[0]?.id;
  let rolePerms = [];
  if (roleId) {
    try {
      const permRes = await api.get(ROLES.BY_ID(roleId));
      rolePerms = extractPermissionNames(permRes);
    } catch (e) {}
  }
  const permissions = mergePermissions(rolePerms, profilePerms);
  dispatch(setPermissions(permissions));
  dispatch(setPermissionsReady(true));
  persistAuthSessionCache(profileUser || userFromBody, permissions, false);
  return profileRes;
};

// Thunks - single place for auth API orchestration
export const checkAuth = (options = {}) => async (dispatch, getState) => {
  const forceRefresh = options?.forceRefresh === true;
  const token = localStorage.getItem('token');
  if (!token) {
    clearAuthSessionCache();
    dispatch(setLoading(false));
    dispatch(setPermissionsReady(true));
    return;
  }

  if (!forceRefresh && checkAuthInFlight) {
    return checkAuthInFlight;
  }

  const run = (async () => {
    try {
      if (!forceRefresh) {
        const cached = getFreshAuthSessionCache(token);
        if (cached) {
          dispatch(setLoading(true));
          dispatch(setCredentials(cached.user));
          localStorage.setItem('user', JSON.stringify(cached.user));
          dispatch(setPermissions(cached.permissions || []));
          dispatch(setPermissionsReady(true));
          if (cached.impersonating) {
            localStorage.setItem('impersonating', 'true');
            dispatch(setImpersonating(true));
          } else {
            localStorage.removeItem('impersonating');
            dispatch(setImpersonating(false));
          }
          return;
        }
      }

      dispatch(setLoading(true));
      dispatch(setPermissionsReady(false));
      const profileRes = await api.get(AUTH.PROFILE);
      const data = profileRes?.data ?? profileRes;
      const userData = data?.user ?? data?.data ?? data;
      const hydratedUser = getState().auth.user;

      // Token is source of truth: always sync user from profile when present. Stale localStorage user
      // (e.g. impersonated account) must not block loading the real user after leave / token swap.
      if (userData && typeof userData === 'object' && (userData.id != null || userData.uuid != null)) {
        dispatch(setCredentials(userData));
        localStorage.setItem('user', JSON.stringify(userData));
      }

      const displayUser =
        userData && typeof userData === 'object' && (userData.id != null || userData.uuid != null)
          ? userData
          : hydratedUser;
      const userId = displayUser?.id ?? displayUser?.uuid;
      const roleId = displayUser?.role?.id || displayUser?.roles?.[0]?.id;

      let profilePerms = extractProfilePermissions(profileRes);
      let rolePerms = [];
      if (roleId) {
        try {
          const permRes = await api.get(ROLES.BY_ID(roleId));
          rolePerms = extractPermissionNames(permRes);
        } catch (e) {
          // Role fetch may fail; use profile perms only
        }
      }

      const permissions = mergePermissions(rolePerms, profilePerms);
      dispatch(setPermissions(permissions));
      dispatch(setPermissionsReady(true));

      if (typeof userData?.impersonating === 'boolean') {
        if (userData.impersonating) {
          localStorage.setItem('impersonating', 'true');
          dispatch(setImpersonating(true));
        } else {
          localStorage.removeItem('impersonating');
          dispatch(setImpersonating(false));
        }
      }

      persistAuthSessionCache(
        displayUser,
        permissions,
        userData?.impersonating === true || localStorage.getItem('impersonating') === 'true'
      );

      if (permissions.length === 0 && userId) {
        toast.warn('User has no permissions assigned');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearAuthSessionCache();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('impersonating');
      localStorage.removeItem(IMPERSONATION_PARENT_TOKEN_KEY);
      dispatch(logoutAction());
    } finally {
      dispatch(setLoading(false));
      dispatch(setPermissionsReady(true));
    }
  })();

  checkAuthInFlight = run;
  try {
    await run;
  } finally {
    if (checkAuthInFlight === run) {
      checkAuthInFlight = null;
    }
  }
};

export const loginUser = (credentials) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    invalidateSessionScopedCaches(dispatch);
    const response = await api.post(AUTH.LOGIN, credentials);
    const { token, user: userFromBody } = extractAuthPayload(response);

    if (!token) {
      throw new Error('No token received from server. Please try again.');
    }

    localStorage.setItem('token', token);
    localStorage.removeItem('impersonating');
    localStorage.removeItem(IMPERSONATION_PARENT_TOKEN_KEY);
    dispatch(setImpersonating(false));
    const userData = userFromBody;
    if (userData && typeof userData === 'object') {
      localStorage.setItem('user', JSON.stringify(userData));
    }

    dispatch(setCredentials(userData || { token }));

    const profileRes = await api.get(AUTH.PROFILE);
    const profileData = profileRes?.data ?? profileRes;
    const profileUser = profileData?.user ?? profileData?.data ?? profileData;
    if (profileUser) {
      dispatch(setCredentials(profileUser));
      localStorage.setItem('user', JSON.stringify(profileUser));
    }

    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = profileUser?.role?.id || profileUser?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(setPermissions(permissions));
    dispatch(setPermissionsReady(true));
    persistAuthSessionCache(profileUser || userData, permissions, false);

    return { data: response?.data ?? response };
  } catch (error) {
    const msg = error.apiMessage || error.response?.data?.message || error?.message || 'Login failed';
    throw new Error(msg);
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Act as another user: POST /users/:id/impersonate — expects token in response (same shape as login).
 */
export const impersonateUser = (userId) => async (dispatch) => {
  try {
    dispatch(setLoading(true));
    invalidateSessionScopedCaches(dispatch);
    const response = await api.post(USERS.IMPERSONATE(userId), {});
    const { token, user: userFromBody } = extractAuthPayload(response);

    if (!token) {
      throw new Error('No token received from server. Impersonation may not be available.');
    }

    const preToken = localStorage.getItem('token');
    if (preToken) {
      localStorage.setItem(IMPERSONATION_PARENT_TOKEN_KEY, preToken);
    }

    localStorage.setItem('token', token);
    const userData = userFromBody;
    if (userData && typeof userData === 'object' && (userData.id || userData.uuid)) {
      localStorage.setItem('user', JSON.stringify(userData));
    }

    dispatch(setCredentials(userData || { token }));

    const profileRes = await api.get(AUTH.PROFILE);
    const profileData = profileRes?.data ?? profileRes;
    const profileUser = profileData?.user ?? profileData?.data ?? profileData;
    if (profileUser) {
      dispatch(setCredentials(profileUser));
      localStorage.setItem('user', JSON.stringify(profileUser));
    }

    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = profileUser?.role?.id || profileUser?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(setPermissions(permissions));
    dispatch(setPermissionsReady(true));

    const data = response?.data ?? response;
    let isImpersonating = false;
    if (typeof profileUser?.impersonating === 'boolean') {
      if (profileUser.impersonating) {
        localStorage.setItem('impersonating', 'true');
        dispatch(setImpersonating(true));
        isImpersonating = true;
      } else {
        localStorage.removeItem('impersonating');
        dispatch(setImpersonating(false));
      }
    } else if (data?.impersonating === true) {
      localStorage.setItem('impersonating', 'true');
      dispatch(setImpersonating(true));
      isImpersonating = true;
    } else {
      localStorage.removeItem('impersonating');
      dispatch(setImpersonating(false));
    }

    persistAuthSessionCache(profileUser || userData, permissions, isImpersonating);

    const label = profileUser?.name || profileUser?.email || 'user';
    toast.success(`You are now signed in as ${label}`);

    return { data: profileRes };
  } catch (error) {
    const msg =
      error.apiMessage || error.response?.data?.message || error?.message || 'Impersonation failed';
    toast.error(msg);
    throw new Error(msg);
  } finally {
    dispatch(setLoading(false));
  }
};

/** End impersonation: POST /users/impersonate/leave — restores original admin session (token + user). */
export const leaveImpersonation = () => async (dispatch, getState) => {
  let usedParentForToken = false;
  const parentSnapshot = localStorage.getItem(IMPERSONATION_PARENT_TOKEN_KEY);
  const preLeaveUser = getState().auth.user;
  const preLeaveKey =
    preLeaveUser?.id != null
      ? String(preLeaveUser.id)
      : preLeaveUser?.uuid != null
        ? String(preLeaveUser.uuid)
        : null;

  try {
    dispatch(setLoading(true));
    invalidateSessionScopedCaches(dispatch);

    let token;
    let userFromBody;

    try {
      const response = await api.post(USERS.IMPERSONATE_LEAVE, {});
      const parsed = extractAuthPayload(response);
      token = parsed.token;
      userFromBody = parsed.user;
      const status = response?.status;
      const parentToken = localStorage.getItem(IMPERSONATION_PARENT_TOKEN_KEY);

      if (!token && parentToken && (status === 204 || status === 200 || status === 201)) {
        token = parentToken;
        usedParentForToken = true;
      }

      if (!token) {
        throw new Error('No token received from server. Could not return to your account.');
      }

      const responseData = response?.data && typeof response.data === 'object' ? response.data : {};
      const impersonationStillSaysTrue = responseData.impersonating === true;
      const bodyUserKey =
        userFromBody?.id != null
          ? String(userFromBody.id)
          : userFromBody?.uuid != null
            ? String(userFromBody.uuid)
            : null;

      // Some backends return `impersonating: false` but echo the impersonated `user` and a token
      // that still resolves to that user (differs from Postman/correct deployments). Prefer the admin
      // JWT we stored before impersonation when the envelope is self-contradictory.
      const buggyLeaveEnvelope =
        !impersonationStillSaysTrue &&
        preLeaveKey &&
        bodyUserKey &&
        preLeaveKey === bodyUserKey &&
        parentToken &&
        token !== parentToken;

      if (buggyLeaveEnvelope) {
        token = parentToken;
        usedParentForToken = true;
        userFromBody = null;
      }
    } catch (leaveErr) {
      if (parentSnapshot) {
        try {
          await hydrateSessionAfterTokenChange(dispatch, parentSnapshot, null);
          toast.warn(
            'Could not complete leave impersonation on the server. Restored your previous login from this browser.'
          );
          return { restoredLocally: true };
        } catch {
          // fall through — show original error
        }
      }
      const msg =
        leaveErr.apiMessage ||
        leaveErr.response?.data?.message ||
        leaveErr?.message ||
        'Could not end impersonation';
      toast.error(msg);
      throw new Error(msg);
    }

    await hydrateSessionAfterTokenChange(dispatch, token, userFromBody);

    const postUser = getState().auth.user;
    const postKey =
      postUser?.id != null
        ? String(postUser.id)
        : postUser?.uuid != null
          ? String(postUser.uuid)
          : null;

    if (
      preLeaveKey &&
      postKey &&
      preLeaveKey === postKey &&
      parentSnapshot &&
      !usedParentForToken
    ) {
      await hydrateSessionAfterTokenChange(dispatch, parentSnapshot, null);
      usedParentForToken = true;
    }

    if (usedParentForToken) {
      toast.success('You are back in your own account. Your session was restored from this browser.');
    } else {
      toast.success('You are back in your own account');
    }
    return { restoredLocally: usedParentForToken };
  } finally {
    dispatch(setLoading(false));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await api.post(AUTH.LOGOUT);
  } catch (e) {
    // ignore
  }
  try {
    await logoutOneSignal();
  } catch (e) {
    // ignore
  }
  // Same full cache wipe as impersonate / leave — auth, lookups, permissions, lead meta, unread.
  invalidateSessionScopedCaches(dispatch);
  localStorage.removeItem(IMPERSONATION_PARENT_TOKEN_KEY);
  localStorage.clear();
  try {
    sessionStorage.removeItem('revivepay_auth_cache');
    sessionStorage.removeItem('revivepay_permissions_catalog');
    sessionStorage.removeItem('revivepay_lookup_cache');
  } catch {
    // ignore
  }
  dispatch(logoutAction());
  toast.success('Logged out');
};

export const refreshPermissions = () => async (dispatch) => {
  try {
    clearAuthSessionCache();
    const profileRes = await api.get(AUTH.PROFILE);
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;

    if (userData && typeof userData === 'object' && (userData.id != null || userData.uuid != null)) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    }

    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = userData?.role?.id || userData?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(refreshPermissionsSuccess(permissions));
    persistAuthSessionCache(
      userData,
      permissions,
      userData?.impersonating === true || localStorage.getItem('impersonating') === 'true'
    );
  } catch (e) {
    console.error('Failed to refresh permissions:', e);
  }
};

export const getProfile = () => async (dispatch, getState) => {
  try {
    const { data } = await api.get(AUTH.PROFILE);
    return data?.user ?? data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Failed to get profile');
  }
};

/** Fetch profile and update Redux auth state (user + permissions) - returns user data */
export const refreshAuthProfile = () => async (dispatch) => {
  try {
    clearAuthSessionCache();
    const profileRes = await api.get(AUTH.PROFILE);
    const data = profileRes?.data ?? profileRes;
    const userData = data?.user ?? data?.data ?? data;
    if (userData) {
      dispatch(setCredentials(userData));
      localStorage.setItem('user', JSON.stringify(userData));
    }
    const profilePerms = extractProfilePermissions(profileRes);
    const roleId = userData?.role?.id || userData?.roles?.[0]?.id;
    let rolePerms = [];
    if (roleId) {
      try {
        const permRes = await api.get(ROLES.BY_ID(roleId));
        rolePerms = extractPermissionNames(permRes);
      } catch (e) {}
    }
    const permissions = mergePermissions(rolePerms, profilePerms);
    dispatch(setPermissions(permissions));
    persistAuthSessionCache(
      userData,
      permissions,
      userData?.impersonating === true || localStorage.getItem('impersonating') === 'true'
    );
    return userData;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Failed to get profile');
  }
};

export const updateProfile = (formData) => async () => {
  formData.append('_method', 'PUT');
  try {
    const { data } = await api.post(AUTH.PROFILE_UPDATE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Profile update failed');
  }
};

export const forgotPassword = (payload) => async () => {
  try {
    const { data } = await api.post(AUTH.FORGOT_PASSWORD, payload);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Forgot password failed');
  }
};

export const resetPassword = (payload) => async () => {
  try {
    const { data } = await api.post(AUTH.RESET_PASSWORD, payload);
    return data;
  } catch (err) {
    throw new Error(err.response?.data?.message || 'Reset password failed');
  }
};
